import { type RefObject } from "react"

import { useFittedTableHeight } from "./use-fitted-table-height.ts"
import { useTableFooterHeight } from "./use-table-footer-height.ts"

interface UseMeasuredTableHeightParams {
  /**
   * The list owns the page, so the scrollport is the only cap on its height.
   * Left unset the body stops at the shared 600px, which is what a table
   * sharing a page with other sections needs.
   */
  fullPage?: boolean
  /**
   * Size the body for the screen the card gets once scrolled to, below this
   * much sticky chrome — for a page carrying a block above the list that is
   * worth scrolling past. See `useFittedTableHeight`.
   */
  fillsViewportBelow?: number
  /**
   * Never leave the body shorter than this many rows. See
   * `useFittedTableHeight`.
   */
  minRows?: number
}

interface MeasuredTableHeight<T extends HTMLElement> {
  /** Goes on the element wrapping both the table and whatever follows it. */
  tableRef: RefObject<T | null>
  /** For the table's `scroll.y`. */
  tableHeight: number
}

/**
 * The two height hooks a scrolled list always wires together, wired once.
 *
 * They are not independent: the footer's height is what the body has to give
 * back, and the container the footer is measured in is the one the body is
 * measured against — so they take the *same* ref, and that ref has to enclose
 * both. Wiring them by hand is a standing chance to reserve a footer nobody
 * measured, and the caller cannot tell, because the mistake degrades quietly
 * into a count pushed under the fold.
 *
 * `useScrolledTable` is this plus an infinite-scroll listener. A paginated list
 * takes this directly rather than feeding that listener a `next` it does not
 * have.
 *
 * Not for a table sharing a scrolling column with another one: the height half
 * fills the space below the body, so the first table would take the whole
 * scrollport. Those keep a fixed height — see the decision in `docs/kb`.
 */
export function useMeasuredTableHeight<T extends HTMLElement = HTMLDivElement>({
  fullPage,
  fillsViewportBelow,
  minRows
}: UseMeasuredTableHeightParams = {}): MeasuredTableHeight<T> {
  const { ref: tableRef, height: footerHeight } = useTableFooterHeight<T>()

  const { height: tableHeight } = useFittedTableHeight<T>({
    containerRef: tableRef,
    reserve: footerHeight,
    fillsViewportBelow,
    minRows,
    ...(fullPage ? { max: Number.MAX_SAFE_INTEGER } : {})
  })

  return { tableRef, tableHeight }
}
